/*
  Set of generic utility functions that can be used across the app
*/

import { WeightUnit } from '~/constants/weight';
import * as convert from 'convert-units';
import { curry } from 'ramda';

/**
 * This function correctly type guards and type narrows for not null or undefined
 * so the TS compiler understands a value exists after the call to this function.
 * @param val any value type
 * @returns true if the value is not undefined or null, false otherwise
 */
export const isNotNil = <T>(val: T): val is Exclude<T, null | undefined> =>
  val !== null && val !== undefined;

/**
 * Curried
 * rounds the number `num` to `precision` decimal points
 * @param num floating point number
 * @returns floating point  number
 */
export const roundFloat = curry((precision: number, num: number): number => {
  const expo = Math.pow(10, precision);
  return Math.round((num + Number.EPSILON) * expo) / expo;
});

/**
 * Curried
 * function that converts the number `val` from the `from` units
 * to the `to` units
 */
export const convertUnits = curry((from: WeightUnit, to: WeightUnit, val: number): number =>
  convert(val).from(from).to(to),
);

interface HasToString {
  toString: () => string;
}

function hasToString(obj: unknown): obj is HasToString {
  return (obj as HasToString).toString !== undefined;
}

export const unknownToString = (input: unknown): string => {
  if (isNotNil(input)) {
    if (hasToString(input)) {
      let value = input.toString();

      if (value === '[object Object]') {
        value = JSON.stringify(input);
      }

      return value;
    }
  }

  return '';
};

export const formatTestIdName = (name: string): string => {
  return name.toLowerCase().trim().replaceAll(' ', '-');
};

/**
 * remove leading and trailing spaces from each number and string value
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const trimObjectValues = (obj: Record<string, any>): Record<string, any> => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: ['string', 'number'].includes(typeof value) ? value.trim() : value,
    };
  }, obj);
};
